<script lang="ts" setup>
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import { i18n } from '@/i18n';
import { ref } from 'vue';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import MultiTagInput from '@/corePagesModule/components/MultiTagInput.vue';
import { BusinessFreeColumnValue, IBusiness } from '@/coreDomainsModule/entities/businessBase';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { useUpdateAggregateBusinessForm } from '@/corePagesModule/updateBusinessModal/composables/useUpdateAggregateBusinessForm';
import {
  formOptionsAggregateBusiness,
  inputTypeKeyMap,
  keyMappingForSelectInput,
  unitOptions,
  validateRuleKeyMap,
} from '@/corePagesModule/updateBusinessModal/constants';
import AddOptionWideSection from './AddOptionWideSection.vue';
import { ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import TableCheckbox from './TableCheckbox.vue';
import {
  AGGREGATE_BUSINESS_NULLABLE_FORM_DEFAULT_STATE,
  AggregateBusinessNullableFormItem,
  AggregateBusinessNullableFormState,
} from '../constants';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';
import { SettingRepository } from '@/coreInfrastructureModule/Repositories/SettingRepository';

const t = i18n.t;

const props = withDefaults(
  defineProps<{
    item?: IBusiness;
  }>(),
  { item: undefined }
);

const {
  isEdited,
  currentOpenSelectInputName,
  fieldState,
  allValidate,
  updateValue,
  updateUserValue,
  addNewOption,
  initializeForm,
  createAggregateBusinessPatch,
} = useUpdateAggregateBusinessForm({
  validateRuleKeyMap: Object.fromEntries(
    Object.entries(validateRuleKeyMap)
      .filter(([key, _]) => (formOptionsAggregateBusiness as unknown as string).includes(key))
      .map(([key, value]): [string, ValidateRule[]] => [key, value.filter((v) => v !== ValidateRule.Required)])
  ),
});

const selectedUserAccounts = ref<UserAccount[]>([]);
const nullableFormState = ref<AggregateBusinessNullableFormState>({ ...AGGREGATE_BUSINESS_NULLABLE_FORM_DEFAULT_STATE });
const businessFreeColumnValues = ref<BusinessFreeColumnValue[]>([]);
const editedBusinessFreeColumnNos = ref(new Set<number>());
const settingRepository = new SettingRepository();

/**
 * ユーザー情報をuserAccountsのまま変更する
 * @param userAccounts - 変更後ユーザー情報
 */
const updateUserAccountsValue = (userAccounts: UserAccount[]) => {
  selectedUserAccounts.value = userAccounts;
};

/**
 * NULLにするチェックボックスのハンドラ
 * @param option - ヘッダー名
 * @param isChecked - チェックの有無
 */
const isChangedNullableCheckbox = (option: string, isChecked: boolean) => {
  nullableFormState.value[option] = isChecked;
  updateValue(option, '');
};

/** 自由欄設定を取得して一括編集フォームに反映する */
const initializeBusinessFreeColumns = async () => {
  const result = await settingRepository.getBusinessFreeColumnSetting();
  if (result.status.type !== 'success') {
    businessFreeColumnValues.value = [];
    return;
  }
  businessFreeColumnValues.value = result.status.data.aggregate
    .map((item, index) => ({
      columnNo: index + 1,
      headerName: item.headerName,
      isMulti: item.isMulti,
      isConfigured: item.isConfigured ?? false,
      value: '',
      target: 'aggregate' as const,
    }))
    .filter((item) => item.isConfigured);
};

/**
 * 自由欄の入力値を更新する
 * @param index - 自由欄配列のindex
 * @param value - 入力値
 */
const updateBusinessFreeColumnValue = (index: number, value: string) => {
  const target = businessFreeColumnValues.value[index];
  if (!target) return;
  target.value = value;
  editedBusinessFreeColumnNos.value.add(target.columnNo);
  isEdited.value = true;
};

/** 変更された自由欄を含む集計一括編集patchを作成する */
const createAggregateBusinessPatchWithFreeColumns = (): Partial<IBusiness> => ({
  ...createAggregateBusinessPatch(),
  businessFreeColumnValues: businessFreeColumnValues.value
    .filter((item) => editedBusinessFreeColumnNos.value.has(item.columnNo))
    .map((item) => ({ ...item })),
});

/**
 * 自由入力カラムの編集状態を変更する
 */
const markBusinessFreeColumnEdited = () => {
  isEdited.value = true;
};

initializeBusinessFreeColumns();

defineExpose({
  fieldState,
  nullableFormState,
  selectedUserAccounts,
  businessFreeColumnValues,
  allValidate,
  isEdited,
  createAggregateBusinessPatch: createAggregateBusinessPatchWithFreeColumns,
  initializeForm,
});
</script>
<template>
  <div class="update-agg-section">
    <div class="update-agg-section__container">
      <template v-for="(option, index) in formOptionsAggregateBusiness" :key="option">
        <div class="item" :class="{ 'item--multiple-row': inputTypeKeyMap[option] === 'selectUser' }">
          <template v-if="inputTypeKeyMap[option] === 'select'">
            <SingleSelectInput
              :index="index"
              label=""
              :model-value="nullableFormState[option] ? t(UNASSIGNED_COLUMN) : fieldState[option].value.value"
              :placeholder="t(option)"
              :filter-type="keyMappingForSelectInput[option]"
              :is-error="fieldState[option]?.isError.value"
              :error-message="fieldState[option]?.errorMessage.value"
              :disabled="nullableFormState[option]"
              disable-unassigned-column
              error-message-position="bottom"
              @click:select-input="() => (currentOpenSelectInputName = option)"
              @update:model-value="(e) => updateValue(option, e)"
            />
          </template>
          <template v-else-if="inputTypeKeyMap[option] === 'selectUser'">
            <MultipleUserSelectInput
              :select-input-index="index"
              label=""
              :placeholder="nullableFormState[option] ? t(UNASSIGNED_COLUMN) : t(option)"
              :original-selected-user-id-list="fieldState[option].value.value ? fieldState[option].value.value.split(',') : []"
              :selected-list-max-height="80"
              :disabled="nullableFormState[option]"
              @update:model-value-user-accounts="updateUserAccountsValue"
              @update:model-value="(e) => updateUserValue(option, e)"
            />
          </template>
          <template v-else-if="inputTypeKeyMap[option] === 'selectUnit'">
            <SingleSelectInput
              :index="index"
              label=""
              :model-value="fieldState[option].value.value"
              :placeholder="t(option)"
              :filter-type="keyMappingForSelectInput[option]"
              :is-error="fieldState[option]?.isError.value"
              :error-message="fieldState[option]?.errorMessage.value"
              :prop-options="unitOptions.map((o) => t(o))"
              error-message-position="bottom"
              @click:select-input="() => (currentOpenSelectInputName = option)"
              @update:model-value="(e) => updateValue(option, e)"
            />
          </template>
          <template v-else>
            <TextInput
              :model-value="nullableFormState[option] ? t(UNASSIGNED_COLUMN) : fieldState[option].value.value"
              label=""
              :placeholder="option === 'MOQ' ? t('取引編集MOQ') : t(option)"
              :is-error="fieldState[option].isError.value"
              :error-message="t(fieldState[option].errorMessage.value)"
              ensure-error-msg-height
              error-message-position="bottom"
              :disabled="nullableFormState[option]"
              @update:model-value="(e) => updateValue(option, e)"
            />
          </template>
          <div v-if="(AggregateBusinessNullableFormItem as readonly string[]).includes(option)" class="item--checkbox">
            <label :for="`cb-${option}`">{{ t('この項目を空白にする') }}</label>
            <TableCheckbox
              :input-id="`cb-${option}`"
              :is-checked="nullableFormState[option]"
              :size="14"
              @change="(isChecked) => isChangedNullableCheckbox(option, isChecked)"
            />
          </div>
        </div>
      </template>
      <template v-for="(freeColumn, index) in businessFreeColumnValues" :key="freeColumn.columnNo">
        <div class="item">
          <MultiTagInput
            v-if="freeColumn.isMulti"
            :model-value="freeColumn.value"
            label=""
            :placeholder="freeColumn.headerName"
            @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
            @editing="markBusinessFreeColumnEdited"
          />
          <TextInput
            v-else
            :model-value="freeColumn.value"
            label=""
            :placeholder="freeColumn.headerName"
            ensure-error-msg-height
            error-message-position="bottom"
            @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
          />
        </div>
      </template>
    </div>
    <!-- 新規選択肢追加 -->
    <AddOptionWideSection is-aggregate-business @set-option="addNewOption" />
  </div>
</template>
<style lang="scss" scoped>
.update-agg-section {
  display: grid;
  gap: 12px;
  &__container {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(10, 1fr);

    padding: 16px;
    border-radius: 8px;
    column-gap: 8px;
    row-gap: 16px;

    background-color: $backgroundDefault;
  }
}
.item {
  display: grid;
  grid-template-columns: 100%;
  height: fit-content;

  box-sizing: border-box;
  border-bottom: solid 1px $borderDefault;

  &--multiple-row {
    grid-row: span 2;
  }

  &--checkbox {
    padding: 4px 4px 0 0;
    display: grid;
    grid-template-columns: 1fr max-content;
    font-size: 12px;
    justify-content: space-between;
    align-items: center;
    user-select: none;
  }
}
</style>
